<script>
import { mapStores } from "pinia";
import { RouterLink } from "vue-router";

import GrantThorntonBadge from "@/assets/img/gdpr/gt-badge.svg";
import { addPackageAndLangParams } from "@/common/route-path-helpers";
import { useAuthStore, useModalStore } from "@/stores";

import IconFacebook from "./icons/white/IconFacebook.vue";
import IconInstagram from "./icons/white/IconInstagram.vue";
import LogoAbc from "./logo/LogoAbc.vue";

export default {
  name: "TheFooter",
  components: {
    LogoAbc,
    IconInstagram,
    IconFacebook,
    RouterLink,
    GrantThorntonBadge
  },
  computed: {
    ...mapStores(useAuthStore, useModalStore),
    isLandingPage() {
      return ["home", "about"].includes(this.$route.name);
    }
  },
  methods: {
    addPackageAndLangParams,
    openHelp() {
      this.modalStore.setModalState("onboarding", true);
    }
  }
};
</script>
<template>
  <footer class="footer">
    <div v-if="isLandingPage" class="footer__expanded">
      <div class="container">
        <div class="row">
          <div class="footer__group col-sm-12 col-lg-3 col-xl-4">
            <div>
              <LogoAbc fill-color="#ffffff" class="footer__logo" />
              <p class="footer__description">{{ $t("footer.description") }}</p>
            </div>
            <div>
              <a
                class="footer__socials"
                target="_blank"
                href="https://www.instagram.com/ordbogencom/"
              >
                <IconInstagram />
              </a>
              <a class="footer__socials" target="_blank" href="https://www.facebook.com/ordbogen">
                <IconFacebook />
              </a>
            </div>
          </div>
          <div class="footer__group col-12 col-sm-12 col-lg-3 col-xl-2">
            <div class="mb-4">
              <h6 class="footer__heading">{{ $t("footer.contactSupport") }}</h6>
              <a class="footer__link" :href="'mailto:' + $t('footer.supportEmail')">{{
                $t("footer.supportEmail")
              }}</a>
            </div>
            <div>
              <h6 class="footer__heading">{{ $t("footer.contactSales") }}</h6>
              <a class="footer__link" :href="'mailto:' + $t('footer.salesEmail')">
                {{ $t("footer.salesEmail") }}
              </a>
            </div>
          </div>
          <div class="footer__group col-12 col-sm-12 col-lg-2 col-xl-2">
            <!-- Logged in -->
            <nav v-if="authStore.isAuthenticated">
              <h6 class="footer__heading">{{ $t("footer.helpfulLinks.heading") }}</h6>
              <RouterLink class="footer__link" :to="{ name: 'dictionary' }">
                {{ $t("footer.helpfulLinks.dictionary") }}
              </RouterLink>
              <RouterLink class="footer__link" :to="addPackageAndLangParams({ name: 'workshop' })">
                {{ $t("footer.helpfulLinks.workshop") }}
              </RouterLink>
              <RouterLink class="footer__link" :to="{ name: 'settings.general' }">
                {{ $t("footer.helpfulLinks.settings") }}
              </RouterLink>
              <a class="footer__link" @click="openHelp()">{{ $t("footer.helpfulLinks.guide") }}</a>
              <a class="footer__link" target="_blank" href="https://drift.ordbogen.com/">
                {{ $t("footer.helpfulLinks.operations") }}
              </a>
            </nav>
            <!-- Not logged in -->
            <nav v-else>
              <h6 class="footer__heading">{{ $t("footer.helpfulLinks.heading") }}</h6>
              <RouterLink
                class="footer__link"
                :to="{ name: 'login', query: { directlyToUnilogin: true } }"
              >
                {{ $t("footer.helpfulLinks.login") }}
              </RouterLink>
              <RouterLink class="footer__link" :to="{ name: 'get-access-today' }">
                {{ $t("footer.helpfulLinks.getAccess") }}
              </RouterLink>
              <a class="footer__link" target="_blank" href="https://drift.ordbogen.com/">
                {{ $t("footer.helpfulLinks.operations") }}
              </a>
            </nav>
          </div>
          <div class="footer__group col-12 col-sm-12 col-lg-2 col-xl-2">
            <nav>
              <h6 class="footer__heading">{{ $t("footer.visit.heading") }}</h6>
              <a class="footer__link" target="_blank" href="https://www.ordbogen.com/">
                {{ $t("footer.visit.ordbogen") }}
              </a>
              <a class="footer__link" target="_blank" href="https://www.grammatip.com/">
                {{ $t("footer.visit.grammatip") }}
              </a>
              <a class="footer__link" target="_blank" href="https://www.writeassistant.com/">
                {{ $t("footer.visit.writeassistant") }}
              </a>
              <a class="footer__link" target="_blank" href="https://careers.ordbogen.com/">
                {{ $t("footer.visit.careers") }}
              </a>
            </nav>
          </div>
          <div class="footer__group col-12 col-sm-12 col-lg-2 col-xl-2">
            <h6 class="footer__heading mb-1">{{ $t("footer.gdprLogoText") }}</h6>
            <GrantThorntonBadge class="footer__gdpr-badge" :alt="$t('footer.gdprLogoAlt')" />
          </div>
        </div>
      </div>
    </div>
    <div class="footer__compact">
      <div class="container">
        <div class="row flex-column flex-sm-row justify-content-between">
          <div
            class="col text-center text-sm-left footer__policy_links justify-content-center justify-content-md-start"
          >
            <p class="footer__text-block d-flex flex-wrap">
              <RouterLink
                class="footer__link mr-2"
                :to="{ name: 'policies', params: { policy: 'tradeTermsBusiness' } }"
              >
                {{ $t("footer.tradeTermsBusiness") }}
              </RouterLink>
              <RouterLink class="footer__link" :to="{ name: 'policies', params: {} }">
                {{ $t("footer.cookiePrivacyPolicy") }}
              </RouterLink>
            </p>
          </div>
          <div class="col text-center text-sm-right footer__copy-wrapper">
            <p class="footer__text-block footer__text">
              {{ $t("footer.copyright", { year: new Date().getFullYear() }) }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>
<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.footer {
  &__policy_links {
    display: flex;
    margin: 0 auto;

    #media-breakpoint-up(md, {
      flex-wrap: wrap;
    });
  }
  &__link {
    width: 100%;

    #media-breakpoint-up(md, {
      width: unset;
    });
  }

  &__copy-wrapper {
    #media-breakpoint-up(sm, {
      align-items: flex-end;
      display: flex;
      justify-content: flex-end;
    });
  }
}
</style>
